/**
 * src/components/documento/AnnotationToolbar.tsx
 * =============================================================================
 * Toolbar flotante a la izquierda del visor. Compuesta por dos paneles
 * independientes (mismo estilo, separados visualmente) — ver
 * [[feedback_simple_outside_complex_inside]]:
 *
 *   Panel principal (siempre visible):
 *     Mano · Seleccionar · ┄ · Texto · Lápiz · Línea · Rect · Elipse · Goma
 *     ┄ · Ojo · Undo · ┄ · +PDF
 *
 *   Panel secundario (aparece a la derecha del principal cuando se usa una
 *   herramienta que necesita opciones, o cuando hay una anotación seleccionada):
 *     Colores · Grosor (si stroke) · Relleno (si forma cerrada)
 *     · Tamaño (si texto) · Borrar (si selección)
 *
 * La separación visual deja claro que el secundario es contextual al tool
 * activo — no son "más opciones globales" mezcladas con las herramientas.
 * =============================================================================
 */

import {
  Circle,
  Crosshair,
  Download,
  Eraser,
  Eye,
  EyeOff,
  FilePlus2,
  Hand,
  Image as ImageIcon,
  MessageCircle,
  Minus,
  MousePointer2,
  PaintBucket,
  Pencil,
  Square,
  Trash2,
  Type,
  Undo2,
} from 'lucide-react'
import {
  ANNOTATION_COLORS,
  STROKE_WIDTHS,
  TEXT_SIZES,
  type Annotation,
  type DrawAnnotation,
  type EllipseAnnotation,
  type LineAnnotation,
  type RectAnnotation,
} from '@/types/annotation'
import type { Tool } from '@/components/documento/AnnotationLayer'
import { CustomColorSwatch } from '@/components/documento/CustomColorSwatch'
import { useTranslation } from 'react-i18next'

/** Tools que dibujan con stroke configurable → muestran el selector de grosor. */
const STROKE_TOOLS: readonly Tool[] = ['draw', 'line', 'rect', 'ellipse']
/** Tools que pintan algo nuevo → necesitan elegir color. */
const DRAW_TOOLS: readonly Tool[] = ['point', 'text', 'draw', 'line', 'rect', 'ellipse']

/** Type guard: anotaciones que tienen strokeWidth. Permite que TS lo estreche. */
type StrokeAnnotation = DrawAnnotation | LineAnnotation | RectAnnotation | EllipseAnnotation
function annotationUsesStroke(a: Annotation): a is StrokeAnnotation {
  return a.type === 'draw' || a.type === 'line' || a.type === 'rect' || a.type === 'ellipse'
}

interface Props {
  tool: Tool
  onToolChange: (tool: Tool) => void
  color: string
  onColorChange: (hex: string) => void
  strokeWidth: number
  onStrokeWidthChange: (w: number) => void
  fillShapes: boolean
  onFillShapesChange: (filled: boolean) => void
  visible: boolean
  onVisibilityToggle: () => void
  canUndo: boolean
  onUndo: () => void
  /**
   * Anotación seleccionada en modo 'select'. Si está presente, los controles
   * de color / grosor / tamaño aplican A ELLA (no al default para nuevas).
   */
  selectedAnnotation: Annotation | null
  /** Actualiza la anotación seleccionada (id + cambios parciales). */
  onUpdateSelected: (changes: Partial<Annotation>) => void
  /** Borra la anotación seleccionada. */
  onDeleteSelected: () => void
  /**
   * Si se pasa, aparece un botón "+ PDF" al final del panel principal para
   * subir una nueva versión sin tener que arrastrarla. El padre gestiona
   * el input de archivo + la subida.
   */
  onAddVersion?: () => void
  /** Texto del título del botón (puede variar según contexto autenticado/share). */
  addVersionLabel?: string
  /**
   * Si se pasa, aparece un botón "Descargar" en la zona de acciones globales.
   * El padre decide qué descargar — lo natural es bajar la versión que el
   * usuario está viendo en ese momento. Cuando llegue el Bloque 2 (anotaciones
   * quemadas), el padre decidirá según el estado `visible` si baja PDF
   * original o con anotaciones.
   */
  onDownload?: () => void
  /** Si downloading=true, el botón muestra spinner y queda deshabilitado. */
  downloading?: boolean
}

export function AnnotationToolbar({
  tool,
  onToolChange,
  color,
  onColorChange,
  strokeWidth,
  onStrokeWidthChange,
  fillShapes,
  onFillShapesChange,
  visible,
  onVisibilityToggle,
  canUndo,
  onUndo,
  selectedAnnotation,
  onUpdateSelected,
  onDeleteSelected,
  onAddVersion,
  addVersionLabel,
  onDownload,
  downloading = false,
}: Props) {
  // i18n: esta barra la ve QUIEN RECIBE un enlace, que puede estar en
  // cualquier país. Antes estaba fija en español, así que un cliente francés
  // veía una página en francés alrededor de una herramienta en español.
  // Las cadenas viven en `visor.*` de cada `common.json`.
  const { t } = useTranslation()
  const etiquetaNuevaVersion = addVersionLabel ?? t('visor.herramientas.nuevaVersion')
  const showStrokeWidth =
    STROKE_TOOLS.includes(tool) ||
    (selectedAnnotation !== null && annotationUsesStroke(selectedAnnotation))
  const showTextSize = tool === 'text' || selectedAnnotation?.type === 'text'
  const selectedShape = selectedAnnotation?.type === 'rect' || selectedAnnotation?.type === 'ellipse'
  const showFill = tool === 'rect' || tool === 'ellipse' || selectedShape
  const showColor = DRAW_TOOLS.includes(tool) || selectedAnnotation !== null

  // El panel secundario aparece cuando hay color (=> hay algo que configurar)
  // O cuando hay selección con opción a borrar. Si no, el panel se oculta.
  const showSecondary = showColor || selectedAnnotation !== null

  const activeColor = selectedAnnotation?.color ?? color
  const activeStrokeWidth =
    selectedAnnotation && annotationUsesStroke(selectedAnnotation)
      ? selectedAnnotation.strokeWidth
      : strokeWidth
  const activeFill = selectedShape ? selectedAnnotation.filled === true : fillShapes

  // Los tres handlers deduplican: repetir el valor vigente (doble clic o
  // doble tap sobre el mismo swatch) no debe disparar onUpdateSelected ni
  // renders/sync extra — en la Surface del dueño ese doble tap "se ponía a
  // pensar" por trabajo redundante encadenado al retraso del doble-tap-zoom.
  function handleColorChange(hex: string) {
    if (hex.toLowerCase() === activeColor.toLowerCase()) return
    if (selectedAnnotation) onUpdateSelected({ color: hex } as Partial<Annotation>)
    else onColorChange(hex)
  }

  function handleStrokeWidthChange(w: number) {
    if (w === activeStrokeWidth) return
    if (selectedAnnotation && annotationUsesStroke(selectedAnnotation)) {
      onUpdateSelected({ strokeWidth: w } as Partial<Annotation>)
    } else {
      onStrokeWidthChange(w)
    }
  }

  function handleTextSizeChange(size: number) {
    if (selectedAnnotation?.type === 'text') {
      if ((selectedAnnotation.fontSize ?? 14) === size) return
      onUpdateSelected({ fontSize: size } as Partial<Annotation>)
    }
    // En modo 'text' sin selección no hay setting global de tamaño — la
    // siguiente anotación de texto que crees usará DEFAULT_TEXT_SIZE.
  }

  function handleFillChange() {
    if (selectedShape) onUpdateSelected({ filled: !activeFill } as Partial<Annotation>)
    else onFillShapesChange(!activeFill)
  }

  return (
    // data-visor-chrome: el gesto de pinch del visor (usePinchZoom) ignora los
    // dedos que aterrizan aquí — la toolbar es chrome, no lienzo.
    // touch-manipulation en el contenedor: desactiva la heurística de
    // doble-tap-zoom del navegador en TODO el chrome (los hijos heredan la
    // intersección de touch-action), que era lo que retrasaba ~300ms cada tap
    // en la Surface. stopPropagation del dblclick: que un doble tap en un
    // botón no llegue al visor (allí resetea el encuadre en modo mano).
    <div
      data-visor-chrome="true"
      onDoubleClick={(e) => e.stopPropagation()}
      className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex items-start gap-2 touch-manipulation"
    >
      {/* ─── Panel principal: solo herramientas + acciones globales ─── */}
      <div className="flex flex-col gap-1.5 p-1.5 bg-white rounded-2xl shadow-md border border-zinc-200">
        <ToolButton
          active={tool === 'hand'}
          onClick={() => onToolChange('hand')}
          label={t('visor.herramientas.mano')}
          icon={<Hand className="h-4 w-4" strokeWidth={1.8} />}
        />
        <ToolButton
          active={tool === 'select'}
          onClick={() => onToolChange('select')}
          label={t('visor.herramientas.seleccionar')}
          icon={<MousePointer2 className="h-4 w-4" strokeWidth={1.8} />}
        />
        {/* Puntero láser: cursor con halo rojo + rastro, sin persistencia.
            Pensado para sesiones de revisión en pantalla compartida. */}
        <ToolButton
          active={tool === 'laser'}
          onClick={() => onToolChange('laser')}
          label={t('visor.herramientas.laser')}
          icon={<Crosshair className="h-4 w-4" strokeWidth={1.8} />}
        />
        <Separator />
        <ToolButton
          active={tool === 'point'}
          onClick={() => onToolChange('point')}
          label={t('visor.herramientas.comentar')}
          icon={<MessageCircle className="h-4 w-4" strokeWidth={1.8} />}
        />
        <ToolButton
          active={tool === 'text'}
          onClick={() => onToolChange('text')}
          label={t('visor.herramientas.texto')}
          icon={<Type className="h-4 w-4" strokeWidth={1.8} />}
        />
        <ToolButton
          active={tool === 'draw'}
          onClick={() => onToolChange('draw')}
          label={t('visor.herramientas.dibujar')}
          icon={<Pencil className="h-4 w-4" strokeWidth={1.8} />}
        />
        <ToolButton
          active={tool === 'line'}
          onClick={() => onToolChange('line')}
          label={t('visor.herramientas.linea')}
          icon={<Minus className="h-4 w-4" strokeWidth={1.8} />}
        />
        <ToolButton
          active={tool === 'rect'}
          onClick={() => onToolChange('rect')}
          label={t('visor.herramientas.rectangulo')}
          icon={<Square className="h-4 w-4" strokeWidth={1.8} />}
        />
        <ToolButton
          active={tool === 'ellipse'}
          onClick={() => onToolChange('ellipse')}
          label={t('visor.herramientas.elipse')}
          icon={<Circle className="h-4 w-4" strokeWidth={1.8} />}
        />
        {/* Imagen: subir una foto/captura y colocarla encima del plano.
            Click sobre el visor con esta tool activa abre el file picker. */}
        <ToolButton
          active={tool === 'image'}
          onClick={() => onToolChange('image')}
          label={t('visor.herramientas.imagen')}
          icon={<ImageIcon className="h-4 w-4" strokeWidth={1.8} />}
        />
        <ToolButton
          active={tool === 'erase'}
          onClick={() => onToolChange('erase')}
          label={t('visor.herramientas.borrarAnotacion')}
          icon={<Eraser className="h-4 w-4" strokeWidth={1.8} />}
        />
        <Separator />
        <ToolButton
          active={false}
          onClick={onVisibilityToggle}
          label={`${visible ? t('visor.herramientas.ocultar') : t('visor.herramientas.mostrar')} (G)`}
          icon={visible ? <Eye className="h-4 w-4" strokeWidth={1.8} /> : <EyeOff className="h-4 w-4" strokeWidth={1.8} />}
          dimmed={!visible}
        />
        <ToolButton
          active={false}
          onClick={onUndo}
          label={t('visor.herramientas.deshacer')}
          icon={<Undo2 className="h-4 w-4" strokeWidth={1.8} />}
          disabled={!canUndo}
        />
        {/* Descarga del PDF. Por ahora baja el original sin anotaciones —
            cuando termine el Bloque 2 (pdf-lib quemando anotaciones), si
            las anotaciones están visibles se bajará la versión con ellas. */}
        {onDownload && (
          <ToolButton
            active={false}
            onClick={onDownload}
            label={t('visor.herramientas.descargar')}
            icon={<Download className="h-4 w-4" strokeWidth={1.8} />}
            disabled={downloading}
          />
        )}
        {onAddVersion && (
          <>
            <Separator />
            <ToolButton
              active={false}
              onClick={onAddVersion}
              label={etiquetaNuevaVersion}
              icon={<FilePlus2 className="h-4 w-4" strokeWidth={1.8} />}
            />
          </>
        )}
      </div>

      {/* ─── Panel secundario contextual ─── */}
      {showSecondary && (
        <div className="flex flex-col gap-1.5 p-1.5 bg-white rounded-2xl shadow-md border border-zinc-200">
          {/* Colores siempre que esté visible. */}
          {showColor && (
            <div className="flex flex-col items-center gap-1">
              {ANNOTATION_COLORS.map((c) => {
                const isActive = c.hex === activeColor
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handleColorChange(c.hex)}
                    aria-label={c.label}
                    title={c.label}
                    className={`h-7 w-7 rounded-full border-2 transition active:scale-95 touch-manipulation ${
                      isActive ? 'border-zinc-900 scale-110' : 'border-white hover:scale-105'
                    }`}
                    style={{ background: c.hex }}
                  />
                )
              })}
              {/* Swatch final: color libre con el picker nativo del navegador.
                  Recuerda el último elegido en localStorage. */}
              <CustomColorSwatch activeColor={activeColor} onPick={handleColorChange} />
            </div>
          )}
          {showStrokeWidth && (
            <>
              <Separator />
              <div className="flex flex-col items-center gap-1">
                {STROKE_WIDTHS.map((w) => {
                  const isActive = activeStrokeWidth === w.value
                  return (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => handleStrokeWidthChange(w.value)}
                      aria-label={t('visor.herramientas.grosor', { valor: w.label })}
                      title={t('visor.herramientas.grosor', { valor: w.label })}
                      className={`h-7 w-7 rounded-full flex items-center justify-center transition active:scale-95 touch-manipulation ${
                        isActive ? 'bg-zinc-100 ring-2 ring-zinc-900' : 'hover:bg-zinc-50'
                      }`}
                    >
                      <span
                        aria-hidden
                        className="block rounded-full bg-zinc-700"
                        style={{ width: w.value + 3, height: w.value + 3 }}
                      />
                    </button>
                  )
                })}
              </div>
            </>
          )}
          {showFill && (
            <>
              <Separator />
              <ToolButton
                active={activeFill}
                onClick={handleFillChange}
                label={t(activeFill ? 'visor.herramientas.quitarRelleno' : 'visor.herramientas.rellenar')}
                icon={<PaintBucket className="h-4 w-4" strokeWidth={1.8} />}
              />
            </>
          )}
          {showTextSize && (
            <>
              <Separator />
              <div className="flex flex-col items-center gap-1">
                {TEXT_SIZES.map((s) => {
                  const isActive = selectedAnnotation?.type === 'text'
                    ? (selectedAnnotation.fontSize ?? 14) === s.value
                    : false
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => handleTextSizeChange(s.value)}
                      aria-label={t('visor.herramientas.tamano', { valor: s.label })}
                      title={t('visor.herramientas.tamano', { valor: s.label })}
                      className={`h-7 w-7 rounded flex items-center justify-center transition active:scale-95 touch-manipulation ${
                        isActive ? 'bg-zinc-100 ring-2 ring-zinc-900' : 'hover:bg-zinc-50'
                      }`}
                    >
                      <span
                        aria-hidden
                        className="text-zinc-700 font-semibold leading-none"
                        style={{ fontSize: s.id === 'sm' ? 10 : s.id === 'md' ? 13 : 16 }}
                      >
                        T
                      </span>
                    </button>
                  )
                })}
              </div>
            </>
          )}
          {selectedAnnotation && (
            <>
              <Separator />
              <ToolButton
                active={false}
                onClick={onDeleteSelected}
                label={t('visor.herramientas.borrarSeleccion')}
                icon={<Trash2 className="h-4 w-4 text-red-600" strokeWidth={1.8} />}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

function Separator() {
  return <div className="h-px w-7 mx-auto bg-zinc-200" />
}

interface ToolButtonProps {
  active: boolean
  onClick: () => void
  label: string
  icon: React.ReactNode
  disabled?: boolean
  dimmed?: boolean
}

function ToolButton({ active, onClick, label, icon, disabled, dimmed }: ToolButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={disabled}
      className={`h-10 w-10 sm:h-8 sm:w-8 rounded-full flex items-center justify-center transition active:scale-95 touch-manipulation ${
        active
          ? 'bg-zinc-900 text-white'
          : `${dimmed ? 'text-zinc-400' : 'text-zinc-700'} hover:bg-zinc-100`
      } disabled:opacity-40 disabled:active:scale-100`}
    >
      {icon}
    </button>
  )
}
